import { useEffect, useMemo, useState } from 'react';
import type { Invoice } from '@billing/validation/invoice';
import { Payor } from 'interfaces';
import { generateGetSignedUrl, useDeleteClaimInvoice } from 'api/action';
import { useTranslation } from '@refinedev/core';
import {
  AspectRatio,
  Loader,
  Group,
  Button,
  Text,
  Flex,
  useComputedColorScheme,
} from '@mantine/core';
import * as Sentry from '@sentry/react';
import { useSelectedClaimStore } from 'store';
import { ClaimInvoiceFaxFlow } from './ClaimInvoiceFaxFlow';
import { getInvoiceAttachmentBucketByRecipient } from 'utils/faxUtils';
import { buttonTextColor } from 'utils/buttonUtils';

interface ClaimInvoicesProps {
  invoices: Invoice[] | null;
  onFaxSentSuccess?: () => void;
  onInvoicesChanged?: () => Promise<void> | void;
  payors: Payor[];
}

export const ClaimInvoices: React.FC<ClaimInvoicesProps> = ({
  invoices,
  onFaxSentSuccess,
  onInvoicesChanged,
  payors = [],
}) => {
  const { translate } = useTranslation();
  const computedColorScheme = useComputedColorScheme();
  const deleteClaimInvoice = useDeleteClaimInvoice();

  const files = useMemo(
    () => (invoices || []).map((i) => i.file).filter(Boolean) as string[],
    [invoices]
  );

  const { selectedClaim } = useSelectedClaimStore();

  const [index, setIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    setIndex(0);
  }, [files.length]);

  const currentInvoice = invoices?.[index];
  const currentFileKey = files[index];
  const currentInvoiceBucket = getInvoiceAttachmentBucketByRecipient();

  useEffect(() => {
    const fetchUrl = async () => {
      if (!currentFileKey) {
        setLoading(false);
        setUrl(null);
        return;
      }
      try {
        setLoading(true);
        const signed = await generateGetSignedUrl({
          bucket: currentInvoiceBucket,
          key: currentFileKey,
        });
        setUrl(signed);
      } catch (err) {
        Sentry.setContext('component', { filename: 'ClaimInvoices.tsx' });
        Sentry.captureException(err);
      } finally {
        setLoading(false);
      }
    };
    fetchUrl();
  }, [currentFileKey, currentInvoiceBucket]);

  if (!files.length) {
    return (
      <Text size="sm" data-testid="claim-invoices-empty">
        {translate(
          'pages:claims.claimInvoices.emptyState',
          'No invoices found for this claim.'
        )}
      </Text>
    );
  }

  const handleDeleteInvoice = async () => {
    if (!currentInvoice?.id) return;
    const shouldDelete = window.confirm(
      translate(
        'pages:claims.claimInvoices.deleteConfirm',
        'Delete this invoice and any attached receipts?'
      )
    );
    if (!shouldDelete) return;

    try {
      await deleteClaimInvoice.mutateAsync({
        invoiceID: currentInvoice.id,
      });
      setIndex((current) => Math.max(0, current - 1));
      await onInvoicesChanged?.();
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  return (
    <Flex direction={'column'} gap={'sm'}>
      <Group justify="space-between" align="center">
        {files.length > 1 ? (
          <Group gap={'md'}>
            <Button
              size="xs"
              c={buttonTextColor(computedColorScheme, index === 0)}
              color="grey"
              radius="md"
              variant="outline"
              onClick={() => setIndex((i) => Math.max(0, i - 1))}
              disabled={index === 0}
              data-testid="invoice-prev"
            >
              {translate('buttons.prev', 'Prev')}
            </Button>
            <Button
              size="xs"
              c={buttonTextColor(
                computedColorScheme,
                index === files.length - 1
              )}
              color="grey"
              radius="md"
              variant="outline"
              onClick={() => setIndex((i) => Math.min(files.length - 1, i + 1))}
              disabled={index === files.length - 1}
              data-testid="invoice-next"
            >
              {translate('pages:claims.claimInvoices.next', 'Next')}
            </Button>
          </Group>
        ) : (
          <div />
        )}
        <Group gap="sm">
          {files.length > 1 ? (
            <Text size="sm" data-testid="invoice-counter">
              {index + 1} / {files.length}
            </Text>
          ) : null}
          <Button
            size="xs"
            color="red"
            radius="md"
            variant="outline"
            onClick={handleDeleteInvoice}
            data-testid="delete-invoice"
          >
            {translate('pages:claims.claimInvoices.delete', 'Delete Invoice')}
          </Button>
        </Group>
      </Group>
      {loading && <Loader />}
      {!loading && url && (
        <>
          <AspectRatio ratio={3 / 3} style={{ width: '100%' }}>
            <iframe
              src={`${url}#toolbar=0&navpanes=0&scrollbar=0`}
              style={{ border: 'none', flex: 1 }}
              title={translate('pages:claims.claimInvoices.iframeTitle', {
                index: index + 1,
                total: files.length,
              })}
            />
          </AspectRatio>
          <ClaimInvoiceFaxFlow
            showFaxControls={false}
            attachmentBucket={currentInvoiceBucket}
            attachmentKey={currentFileKey || ''}
            claimID={currentInvoice?.claimID || selectedClaim?.id}
            payors={payors}
            claimPayorID={selectedClaim?.payorID}
            onSuccess={onFaxSentSuccess}
          />
        </>
      )}
    </Flex>
  );
};
